import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router'
import routes from '~pages'
import { useUserStore } from '@/stores/user';
import {useAuthStore} from "@/stores/auth";
import { reloadOnStaleChunk } from '@/utils/reloadOnStaleChunk';
import { Preferences } from '@capacitor/preferences';
import {tokenAlive} from '@/plugins/jwtHelper'
import { refreshTokens, silentReAuth } from '@/plugins/authService';

// DEVAMS-2025: usePermissionsStore is lazy-imported inside the permissions
// guard to avoid a circular module-load chain (router → permissionsStore →
// jwtInterceptor → main → router). The store factory is invoked normally at
// guard execution time.

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...setupLayouts(routes),
  ],
})

router.beforeEach(async(to, from,next) =>  {
  // If running inside an OAuth popup (opened via window.open()), skip the auth
  // guard entirely. The @capacitor-community/generic-oauth2 plugin manages this
  // window and polls popup.location.href to extract the ?code= parameter.
  // Running the guard here would redirect to /login-azure and clobber the code
  // before the plugin can read it (DEVAMS-1831).
  if (window.opener !== null) {
    next();
    return;
  }

  console.debug("ROUTER: going to: "+ to.name);
  const userStore = useUserStore();
  // login not requiring valid login,
  if(to.name !== "login-azure") {
    // auth required
    const isTokenValid = await Preferences.get({key: "tokenExp"}).then(r => {
      return tokenAlive(r.value)
    }) || await Preferences.get({ key: "access_token" }).then(r => {
      if (r.value === "demoaccessToken") {
        userStore.setDemoAccount();
        return true;
      }
      return false;
    });

    console.debug("ROUTER: Is token valid: "+ isTokenValid)

    if (to.name !== 'login-azure' &&
        isTokenValid &&
        (await Preferences.get({ key: "access_token" })).value !== "demoaccessToken" &&
        !userStore.getUserInfo
    ) {
        console.debug("ROUTER: User data missing, renewing...")
        await userStore.renewData();
    }

    if (!isTokenValid){
      const refreshToken = await Preferences.get({key: "refresh_token"}).then(r => {return r.value})
      console.debug("ROUTER: Refresh token: "+ refreshToken)

      if (refreshToken !== undefined && refreshToken !== null && refreshToken !== "") {
        const authStore = useAuthStore();

        // If jwtInterceptor (or another guard instance) is already mid-refresh,
        // wait for it to complete. The promise resolves with the new token on
        // success and rejects on failure.
        if (authStore.getTokenRefreshingInProgress) {
          console.debug("ROUTER: refresh already in progress, waiting...");
          try {
            await authStore.waitForTokenRefresh();
            console.debug("ROUTER: refresh completed, proceeding");
            next();
          } catch (e) {
            console.warn("ROUTER: in-progress refresh failed, redirecting to login", e);
            await Preferences.remove({key: 'access_token'});
            await Preferences.remove({key: 'refresh_token'});
            next({ name: 'login-azure' });
          }
          return;
        }

        // Set the flag BEFORE calling refreshTokens so that any concurrent 401
        // responses from in-flight API calls are queued rather than triggering
        // a second parallel refresh with the same (about to be rotated) token.
        authStore.setTokenRefreshing(true);
        console.debug("ROUTER: Renewing token via refresh token")
        try {
          const tokenData = await refreshTokens(refreshToken);
          console.debug("ROUTER: Token refreshed, redirecting to: " + to.fullPath);
          // Populate user data BEFORE resolving waiters so components that
          // unblock can immediately access user info without a second fetch.
          if (!userStore.getUserInfo) {
            await userStore.renewData();
          }
          // Resolve all waiters (jwtInterceptor queued requests) with the new
          // token directly — bypasses Preferences bridge latency on Capacitor.
          authStore.resolveTokenRefresh(tokenData.access_token);
          next();
        } catch (e) {
          console.warn("ROUTER: Token refresh failed", e?.reason ?? e);
          // Before giving up and redirecting to login, attempt a silent re-auth.
          // If the user has an active Azure AD browser session, prompt=none will
          // silently issue new tokens without any user interaction.
          try {
            console.debug("ROUTER: Attempting silent re-auth (prompt=none)");
            const silentData = await silentReAuth();
            console.debug("ROUTER: Silent re-auth succeeded, continuing navigation");
            if (!userStore.getUserInfo) {
              await userStore.renewData();
            }
            authStore.resolveTokenRefresh(silentData.access_token);
            next();
          } catch (silentErr) {
            console.warn("ROUTER: Silent re-auth failed, redirecting to login", silentErr?.reason ?? silentErr);
            authStore.rejectTokenRefresh(silentErr);
            authStore.setLoginStatus(false);
            await Preferences.remove({key: 'access_token'});
            await Preferences.remove({key: 'refresh_token'});
            next({ name: 'login-azure' });
          }
        }
        return;
      }
    }

    if (!isTokenValid && to.name !== "login-azure") {
      console.debug("ROUTER: Expired/invalid token. Redirecting to login to get new one.")
      next({
        name: 'login-azure',
      })
      return
    }
    else {
      console.debug("ROUTER: Is the token active/valid at this moment: "+ isTokenValid)
      console.debug("ROUTER: Requested route name: "+ to.name)
      next()
    }
    return
  }

  console.debug("ROUTER: User was redirected to login page, no token required.")
  next()
})

// DEVAMS-2025: ensure the permissions store is populated before any gated
// route renders. The page-level checkAccessPage() guards rely on permissions
// being loaded synchronously at <script setup> time. Running this here also
// gives us a clean failure mode: if /me/permissions cannot be reached, the
// user is routed to /permissions-unavailable instead of seeing a misleading
// access-denied snackbar.
router.beforeEach(async (to, from, next) => {
  // Skip for non-authenticated paths and the failure page itself, otherwise
  // /permissions-unavailable would be unreachable in a degraded state.
  if (to.name === 'login-azure' || to.name === 'permissions-unavailable') {
    next();
    return;
  }
  const userStore = useUserStore();
  const authStore = useAuthStore();
  await authStore.restored;
  if (!authStore.getIsLoggedInState) {
    // Not logged in — auth guard above will redirect to login-azure.
    next();
    return;
  }
  const { usePermissionsStore } = await import('@/stores/permissionsStore');
  const permissionsStore = usePermissionsStore();
  // Already loaded (cached on a prior route, or refreshed by Mercure or focus).
  if (permissionsStore.loaded) {
    next();
    return;
  }
  // Avoid stacking parallel fetches if a prior in-flight request is resolving.
  if (permissionsStore.loading) {
    next();
    return;
  }
  try {
    await permissionsStore.fetchPermissions();
    if (permissionsStore.usingFallback) {
      // Skip the retry if the session has already been invalidated (e.g. refresh token
      // expired): the auth guard will redirect to login. Only retry for clock-skew
      // failures where the user is still authentically logged in.
      if (!authStore.getIsLoggedInState) {
        next();
        return;
      }
      // First attempt landed in fallback mode (e.g. Azure AD clock skew: freshly-issued
      // tokens rejected by the BE for ~10 s). Wait 3 s and retry once — if the server
      // clock has caught up the retry succeeds and usingFallback is cleared.
      console.warn('ROUTER: Permissions loaded in fallback mode, retrying in 3 s…');
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (!authStore.getIsLoggedInState) {
        next();
        return;
      }
      await permissionsStore.fetchPermissions();
    }
    next();
  } catch (e) {
    console.warn('ROUTER: Unexpected error loading permissions', e);
    next();
  }
});

// Safety net for stale chunk errors that reach the router (DEVAMS-1844).
// Uses the shared reloadOnStaleChunk() guard so this handler does not race
// with the vite:preloadError / unhandledrejection handlers in main.ts.
router.onError((error) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    reloadOnStaleChunk()
  }
})

// Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
export default router
